import { useState, useEffect } from 'react'
import { Basic } from 'unsplash-js/dist/methods/photos/types'
import { Photos } from 'unsplash-js/dist/methods/search/types/response'
import { reportError } from 'utils/error'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

export type UnsplashImg = {
  thumb: string
  regular: string
  width: number
  height: number
}

const mapResponseToImages = (response: Basic[]): UnsplashImg[] => {
  return response.map((item: Basic) => {
    return {
      thumb: item.urls.thumb,
      regular: item.urls.regular,
      width: item.width,
      height: item.height,
    }
  })
}

interface Props {
  query: string
  initialCount?: number
}

export const useUnsplashPhotos = ({ query, initialCount = 6 }: Props) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)
  const [results, setResults] = useState<UnsplashImg[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const showNoResults = query.length > 0 && results.length === 0 && !isLoading

  const getPhotos = async (nextPage?: number) => {
    const page = nextPage || currentPage

    try {
      const response = await sendToApi<Photos>(`/unsplash/photos?query=${query}&page=${page}&per_page=24`)

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Failed to load photos')
      }

      const results = response.data.results
      const totalPages = response.data.total_pages

      if (!results) return

      const images = mapResponseToImages(results)
      if (totalPages != null) setMaxPage(totalPages)

      return images
    } catch (error) {
      reportError(error, { severity: 'error' })
    }
  }

  const loadPhotos = async () => {
    try {
      setIsLoading(true)
      let images: UnsplashImg[] = []
      if (query.length > 0) {
        const photos = await getPhotos()
        if (!photos) return
        images = photos
      } else {
        const response = await sendToApi<Basic[]>(`/unsplash/random?count=${initialCount}`)

        if (response.type !== SUCCESS_RESPONSE_TYPE || !response.data) {
          throw new Error('Failed to load photos')
        }

        images = mapResponseToImages(response.data)
      }
      if (images) setResults(images)
    } catch (error) {
      reportError(error, { severity: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPhotos()
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowMore = () => {
    setIsLoading(true)
    getPhotos(currentPage + 1).then((urls) => {
      if (urls) setResults([...results, ...urls])
    })
    setCurrentPage(currentPage + 1)
    setIsLoading(false)
  }

  return {
    results,
    isLoading,
    showNoResults,
    handleShowMore,
    currentPage,
    maxPage,
  }
}
